import React, { useCallback, useEffect, useState } from 'react'

import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material'
import { Button, LinearProgress, useNotify, useRefresh, useTranslate } from 'react-admin'

import genericStyles from './../../generics.module.scss'
import { API } from './../../helpers/Api.helper'
import { Modal } from './../../types/Modal'
import styles from './AutoAssignProductsModal.module.scss'

export const AutoAssignProductsModal = ({
  isModalOpen,
  setIsModalOpen,
  importId,
  onChange,
}: AutoAssignProductsModalProps): JSX.Element => {
  const notify = useNotify()
  const translate = useTranslate()
  const refresh = useRefresh()

  const [isLoading, setIsLoading] = useState(true)
  const [isFinished, setIsFinished] = useState(false)
  const [previewedRows, setPreviewedRows] = useState<AutoCorrectProductPreview[]>([])
  const [autoCorrectJobId, setAutoCorrectJobId] = useState<string>()
  const [progress, setProgress] = useState(0)

  const confirmAutoCorrect = useCallback(async () => {
    try {
      const response = await API.post(`/api/youtubeRawImports/${importId}/autoCorrectImportJob`, {})

      setAutoCorrectJobId(response.data.job.id)
      setProgress(response.data.job.progress)
      setIsLoading(true)
    } catch (e) {
      console.error(`Error - auto correct product - ${e}`)
      notify(
        `${translate(
          'pages.importsAmendment.autoAssignProductsModal.toast_autoAssignProductsError',
        )} - ${e}`,
        {
          type: 'error',
        },
      )
    }
  }, [translate, notify, importId])

  const refreshAutoCorrectJobStatus = useCallback(async () => {
    if (autoCorrectJobId && isLoading) {
      try {
        const response = await API.get(
          `/api/youtubeRawImports/${importId}/autoCorrectImportJob/${autoCorrectJobId}`,
          {},
        )

        const p = (response.data as any)?.job.progress

        setProgress(p)
        if (p >= 100) {
          refresh()
          onChange()
          setIsLoading(false)
          setIsFinished(true)

          notify(
            translate(
              'pages.importsAmendment.autoAssignProductsModal.toast_autoAssignProductsSuccess',
            ),
            {
              type: 'success',
            },
          )
        }
      } catch (e: any) {
        setIsLoading(false)
        setAutoCorrectJobId(undefined)
        setProgress(0)
        setIsFinished(true)

        notify(
          `${translate('pages.importsAmendment.allArtworksCreationError')} - ${
            e.response.data?.message || e.response.data
          }`,
          { type: 'error' },
        )
      }
    }
  }, [autoCorrectJobId, isLoading, importId, onChange, notify, refresh, translate, setIsModalOpen])

  const onClose = useCallback(() => {
    setIsModalOpen(false)
    setAutoCorrectJobId(undefined)
    setProgress(0)
  }, [setIsModalOpen])

  useEffect(() => {
    if (isModalOpen) {
      const getPreview = async () => {
        const res = await API.get<AutoCorrectProductPreview[]>(
          `/api/youtubeRawImports/${importId}/autoCorrectImportPreview`,
        )
        setPreviewedRows(res.data)
      }

      setIsLoading(true)
      getPreview()
      setIsLoading(false)
    }
  }, [importId, isModalOpen])

  useEffect(() => {
    const interval = setInterval(() => {
      refreshAutoCorrectJobStatus()
    }, 200)

    return () => clearInterval(interval)
  }, [refreshAutoCorrectJobStatus])

  return (
    <Dialog aria-labelledby="autocorrect-product-modal" open={isModalOpen}>
      <DialogTitle id="autocorrect-product-modal" className={genericStyles.modalTitle}>
        {translate('pages.importsAmendment.autoAssignProductsModal.title')}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" color="textPrimary">
          {translate('pages.importsAmendment.autoAssignProductsModal.description')}
        </Typography>
        <Grid container direction="column" spacing={0} justifyContent="center" alignItems="center">
          <Grid item width="100%">
            {isLoading && progress === 0 ? (
              <div className={styles.loader}>
                <CircularProgress size="2rem" />
              </div>
            ) : (
              <ul
                style={{
                  paddingLeft: previewedRows.length === 0 ? 0 : 'initial',
                  textAlign: previewedRows.length === 0 ? 'center' : 'initial',
                }}
              >
                {previewedRows.length === 0 ? (
                  <li className={styles.nopreview}>
                    <b>{translate('pages.importsAmendment.autoAssignProductsModal.noPreview')}</b>
                  </li>
                ) : (
                  previewedRows.map(row => {
                    const translationKey = row.defaultProductName
                      ? 'pages.importsAmendment.autoAssignProductsModal.rowWithDefaultProduct'
                      : 'pages.importsAmendment.autoAssignProductsModal.rowWithoutDefaultProduct'

                    return (
                      <li
                        key={row.beneficiaryId}
                        dangerouslySetInnerHTML={{
                          __html: translate(translationKey, {
                            ...row,
                            smart_count: row.count,
                          }),
                        }}
                      />
                    )
                  })
                )}
              </ul>
            )}
          </Grid>
          <Grid item width="80%">
            <LinearProgress
              variant="determinate"
              value={progress}
              color={progress >= 100 ? 'success' : 'primary'}
              style={{
                width: '100%',
                height: '0.5rem',
                padding: '0 0.5rem',
                opacity: progress === 0 ? 0 : 1,
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {!isFinished && previewedRows.length > 0 && (
          <>
            <Button onClick={() => onClose()} label={translate('_generics.cancel')} />
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                confirmAutoCorrect()
              }}
              label={translate('_generics.confirm')}
              disabled={isLoading}
            />
          </>
        )}
        {(isFinished || previewedRows.length === 0) && (
          <Button onClick={() => onClose()} label={translate('_generics.close')} />
        )}
      </DialogActions>
    </Dialog>
  )
}

type AutoAssignProductsModalProps = Modal & {
  importId: string
  onChange: () => void
}

type AutoCorrectProductPreview = {
  beneficiaryId: string
  beneficiaryName: string
  defaultProductId: string | undefined
  defaultProductName: string | undefined
  defaultProductTypeId: string
  defaultProductTypeName: string
  count: number
}
